import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { CheckoutResponseSchema, type ICheckout } from '~/schemas/checkout/checkout'
import type { ICardAttempts } from '~/schemas/checkout/payment'

/**
 * Checkout em andamento (pedido, formas de pagamento e cupom), como devolvido pelas rotas `checkout/*` e já validado
 * pelo `CheckoutResponseSchema`. Os services do domínio (`services/domain/checkout`) gravam aqui a resposta de cada
 * chamada com `setCheckout`, no `onSuccess`; os hooks e as telas só leem.
 *
 * Persiste com a chave de sempre (`checkout-storage`) e o formato de sempre (`{ data }`): na reidratação o `data`
 * passa pelo schema; o que não passa vira `null`, e a tela recarrega pelo `checkout/create`. Não há dado de cartão
 * aqui (a API não devolve).
 */
/** Tentativas de cartão conhecidas de um pedido (a partir da primeira recusa). */
export interface IOrderCardAttempts {
    orderNumber: string
    attempts: ICardAttempts
}

interface ICheckoutState {
    checkout: ICheckout | null
    /**
     * Tentativas de cartão do pedido, gravadas pelo service do finish na recusa. Ficam na store (e não no formulário,
     * que é desmontado ao fechar) e fora do localStorage: o `partialize` persiste só o checkout.
     */
    cardAttempts: IOrderCardAttempts | null
    setCheckout: (checkout: ICheckout) => void
    setCardAttempts: (orderNumber: string, attempts: ICardAttempts) => void
    clearCheckout: () => void
    resetStore: () => void
}

const initialState = {
    checkout: null as ICheckout | null,
    cardAttempts: null as IOrderCardAttempts | null,
}

/** Checkout guardado (cru, de uma versão antiga, ou já validado; o schema é idempotente), ou `null`. */
function fromPersisted(raw: unknown): ICheckout | null {
    const parsed = raw ? CheckoutResponseSchema.safeParse(raw) : null
    return parsed?.success ? parsed.data : null
}

export const useCheckoutStore = create<ICheckoutState>()(
    devtools(
        persist(
            (set) => ({
                ...initialState,
                setCheckout: (checkout) => set({ checkout }, false, 'setCheckout'),
                setCardAttempts: (orderNumber, attempts) =>
                    set({ cardAttempts: { orderNumber, attempts } }, false, 'setCardAttempts'),
                clearCheckout: () => set(initialState, false, 'clearCheckout'),
                resetStore: () => set(initialState, false, 'resetStore'),
            }),
            {
                name: 'checkout-storage',
                partialize: (state) => ({ data: state.checkout }),
                merge: (persisted, current) => ({
                    ...current,
                    checkout: fromPersisted((persisted as { data?: unknown } | null)?.data),
                }),
            },
        ),
        { name: 'checkout-store' },
    ),
)

/** Tentativas de cartão do pedido em andamento (as de outro pedido não valem), ou `null`. */
export const selectCardAttempts = (state: Pick<ICheckoutState, 'checkout' | 'cardAttempts'>): ICardAttempts | null =>
    state.cardAttempts && state.cardAttempts.orderNumber === state.checkout?.order.orderNumber
        ? state.cardAttempts.attempts
        : null
